import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import API, { API_VERSION, REQUEST_METHOD } from '../../../api'
import STATUS from '../../../common/constants/reducer_statuses'

export const initialState = {
  availableProductTags: {
    data: [],
    status: STATUS.IDLE,
  },
  productAssignmentRules: {
    data: [],
    status: STATUS.IDLE,
    byProductId: {},
  },
}

export const fetchAvailableProductTags = createAsyncThunk(
  'product/fetchAvailableProductTags',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API('fee/availableProductTags', API_VERSION.TWO).request(REQUEST_METHOD.GET)
      return response
    } catch (err) {
      return rejectWithValue(err.errors._error)
    }
  },
)

export const fetchProductAssignmentRules = createAsyncThunk(
  'product/fetchProductAssignmentRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API('fee/product-assignment-rules', API_VERSION.TWO).get()
      return response.results || response
    } catch (err) {
      return rejectWithValue(err.errors?._error || err)
    }
  },
)

export const fetchProductAssignmentRuleByProductId = createAsyncThunk(
  'product/fetchProductAssignmentRuleByProductId',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await API(`fee/product-assignment-rules/${productId}`, API_VERSION.TWO).get()
      return response.result ?? null
    } catch (err) {
      if (err?.status === 404) {
        return null
      }
      return rejectWithValue(err?.errors?._error || err)
    }
  },
)

export const createProductAssignmentRules = createAsyncThunk(
  'product/createProductAssignmentRules',
  async ({ product_id, combinator, rules }, { rejectWithValue }) => {
    try {
      const response = await API(`fee/product-assignment-rules/${product_id}`, API_VERSION.TWO).post({
        combinator: combinator || 'and',
        rules: rules || [],
      })
      const created = response.result
      return created
    } catch (err) {
      return rejectWithValue(err.errors?._error || err)
    }
  },
)

export const updateProductAssignmentRules = createAsyncThunk(
  'product/updateProductAssignmentRules',
  async ({ productId, combinator, rules }, { rejectWithValue }) => {
    try {
      const response = await API(`fee/product-assignment-rules/${productId}`, API_VERSION.TWO).put({
        combinator,
        rules,
      })
      return response.result
    } catch (err) {
      return rejectWithValue(err.errors?._error || err)
    }
  },
)

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    resetProductState: () => initialState,
  },
  extraReducers: {
    [fetchAvailableProductTags.pending]: (state) => {
      state.availableProductTags.status = STATUS.LOADING
    },
    [fetchAvailableProductTags.fulfilled]: (state, action) => {
      state.availableProductTags.status = STATUS.SUCCEEDED
      state.availableProductTags.data = action.payload
    },
    [fetchAvailableProductTags.rejected]: (state) => {
      state.availableProductTags.status = STATUS.FAILED
    },
    [fetchProductAssignmentRules.pending]: (state) => {
      state.productAssignmentRules.status = STATUS.LOADING
    },
    [fetchProductAssignmentRules.fulfilled]: (state, action) => {
      state.productAssignmentRules.status = STATUS.SUCCEEDED
      state.productAssignmentRules.data = Array.isArray(action.payload) ? action.payload : []
    },
    [fetchProductAssignmentRules.rejected]: (state) => {
      state.productAssignmentRules.status = STATUS.FAILED
    },
    [fetchProductAssignmentRuleByProductId.fulfilled]: (state, action) => {
      const productId = action.meta.arg
      if (productId != null) {
        state.productAssignmentRules.byProductId[String(productId)] = action.payload
      }
    },
    [createProductAssignmentRules.fulfilled]: (state, action) => {
      if (action.payload) {
        const existing = state.productAssignmentRules.data || []
        state.productAssignmentRules.data = [...existing, action.payload]
        const pid = action.payload.product_id
        if (pid != null) {
          state.productAssignmentRules.byProductId[String(pid)] = action.payload
        }
      }
    },
    [updateProductAssignmentRules.fulfilled]: (state, action) => {
      if (action.payload) {
        const list = state.productAssignmentRules.data || []
        const idx = list.findIndex((item) => String(item.product_id) === String(action.payload.product_id))
        if (idx >= 0) {
          const next = [...list]
          next[idx] = action.payload
          state.productAssignmentRules.data = next
        } else {
          state.productAssignmentRules.data = [...list, action.payload]
        }
        const pid = action.payload.product_id
        if (pid != null) {
          state.productAssignmentRules.byProductId[String(pid)] = action.payload
        }
      }
    },
  },
})

export default productSlice.reducer
